/**
 * Prototype comments — API client for reviewer feedback on clickable
 * prototypes (backend app/api/routes/prototype_comments.py). Staff-only.
 */
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { saveCsvResponse } from "@/lib/campaigns/types";

const BASE = `${API_BASE}/api/v1/prototype-comments`;

export interface PrototypeComment {
  id: string;
  prototype_key: string;
  screen_key: string;
  anchor_label: string | null;
  body: string;
  author_id: string | null;
  author_name: string | null;
  created_at: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  resolution_note: string | null;
}

async function req(path: string, options: RequestInit = {}) {
  const res = await fetchWithAuth(`${BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = typeof data.detail === "string" ? data.detail : `Request failed (${res.status})`;
    throw Object.assign(new Error(detail), { status: res.status });
  }
  return res.status === 204 ? null : res.json();
}

export const listComments = (prototype: string): Promise<PrototypeComment[]> =>
  req(`?prototype=${encodeURIComponent(prototype)}`);

export const createComment = (body: { prototype_key: string; screen_key: string; anchor_label?: string | null; body: string }): Promise<PrototypeComment> =>
  req("", { method: "POST", body: JSON.stringify(body) });

export const resolveComment = (id: string, resolution_note: string): Promise<PrototypeComment> =>
  req(`/${id}/resolve`, { method: "PATCH", body: JSON.stringify({ resolution_note }) });

export const reopenComment = (id: string): Promise<PrototypeComment> =>
  req(`/${id}/reopen`, { method: "PATCH" });

export const deleteComment = (id: string): Promise<null> =>
  req(`/${id}`, { method: "DELETE" });

export async function exportComments(prototype: string): Promise<void> {
  const res = await fetchWithAuth(`${BASE}/export.csv?prototype=${encodeURIComponent(prototype)}`);
  await saveCsvResponse(res, `${prototype.replace(/\./g, "_")}_feedback.csv`);
}
